// @flow strict

import * as React from 'react';
import styles from './styles.scss';
import PageContentBlock from '../../internal/PageContentBlock';
import FormattedContent from '../../internal/FormattedContent';
import type { BlockRichText } from '../../internal/types';

const blockVariantMap = {
  Default: 'DefaultContrast',
  Dark: 'Dark',
};

type Question = {
  title: string,
  children: BlockRichText,
};

type PropTypes = {|
  title: string,
  questions: Array<Question>,
  background: 'Default' | 'Dark',
|};

// Decode HTML Entities
const decodeHtmlEntities = (html: string): string => {
  if (!html) return '';

  if (typeof document !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body?.textContent || '';
  }
  return '';
};

// Extract plain text from HTML
const extractPlainText = (html: string): string => {
  if (!html) return '';
  const plainText = html.replace(/<\/?[^>]+(>|$)/g, ''); // Remove HTML tags
  return decodeHtmlEntities(plainText);
};

// Splits the FAQ list into two columns (odd/even index)
const splitArray = (array: Array<Question>) => {
  return array.reduce(
    (acc, val, index) => {
      const key: string = index % 2 ? 'odd' : 'even';
      return {
        ...acc,
        [key]: acc[key].concat(val),
      };
    },
    { odd: [], even: [] },
  );
};

// Component for rendering each column of FAQs
const Col = ({ col, background }: { col: Array<Question>, background: 'Default' | 'Dark' }) => {
  return (
    <div className={styles.col}>
      {col.map((question, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className={styles.question} key={index}>
          <h3 className={styles.questionTitle}>{question.title}</h3>
          <FormattedContent
            isDarkBackground={background === 'Dark'}
            className={styles.questionText}
          >
            {question.children}
          </FormattedContent>
        </div>
      ))}
    </div>
  );
};

const Faq = (props: PropTypes) => {
  const { odd, even } = splitArray(props.questions);
  const [structuredData, setStructuredData] = React.useState(null);

  React.useEffect(() => {
    if (props.questions.length === 0) {
      setStructuredData(null);
      return;
    }

    const processedData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: props.questions.map(({ title, children }) => {
        // Ensure children is a string before extracting plain text
        const childrenStr = typeof children === 'string' ? children : String(children);
        const answerText = extractPlainText(childrenStr) || 'No content available';

        return {
          '@type': 'Question',
          name: title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: answerText,
          },
        };
      }),
    };

    setStructuredData(processedData);
  }, [props.questions]);

  return (
    <>
      <PageContentBlock background={blockVariantMap[props.background]} title={props.title}>
        <div>
          <div className={styles.questionContainer}>
            <Col col={even} background={props.background} />
            <Col col={odd} background={props.background} />
          </div>
        </div>
      </PageContentBlock>

      {/* Inject Structured Data only if FAQs exist */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
    </>
  );
};

Faq.defaultProps = {
  background: 'Default',
};

export default Faq;
